/**
 * Web binding: the shared hook plus the one browser-specific bit — turning a
 * `File` from an <input> into bytes with its intrinsic dimensions.
 */
import { useCallback, useMemo } from 'react';
import { useVav as useVavCore } from '@vav/client-core';
import { fromB64 } from '@vav/protocol';
import type { Recording } from './recorder.ts';
import { createStore } from './idbStore.ts';

export type { Phase, IdentityAlert } from '@vav/client-core';

/**
 * The store depends on which profile is active, so it cannot be a module
 * constant any more. It still has to be stable across renders — the hook calls
 * it once on mount and rebuilding it every render would reopen the database.
 */
function storeFor(profileId: string) {
  return () => createStore(profileId);
}

/** Invite code from the link the user followed, e.g. /?invite=ABC123. */
function inviteFromUrl(): string | undefined {
  return new URLSearchParams(window.location.search).get('invite') ?? undefined;
}

const INVITE_CODE = inviteFromUrl();

export function useVav(serverUrl: string, profileId: string) {
  // App.tsx remounts this whole subtree on a profile switch, so the identity of
  // this callback only has to survive renders within one profile.
  const createStoreForProfile = useMemo(() => storeFor(profileId), [profileId]);
  const vav = useVavCore({ serverUrl, createStore: createStoreForProfile, inviteCode: INVITE_CODE });

  const sendFile = useCallback(
    async (file: File, caption?: string) => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const dimensions = file.type.startsWith('image/') ? await imageSize(file) : {};
      await vav.sendMedia(
        bytes,
        { mime: file.type || 'application/octet-stream', name: file.name, ...dimensions },
        caption,
      );
    },
    [vav],
  );

  const sendVoice = useCallback(
    async (recording: Recording) => {
      await vav.sendMedia(recording.bytes, {
        mime: recording.mime,
        // Extension only matters if the file is ever saved out; the mime type
        // is what actually drives playback.
        name: `voice-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${extensionFor(recording.mime)}`,
        durationMs: recording.durationMs,
        voice: true,
        waveform: fromB64(recording.waveform),
      });
    },
    [vav],
  );

  return { ...vav, sendFile, sendVoice };
}

function extensionFor(mime: string): string {
  if (mime.includes('mp4')) return 'm4a';
  if (mime.includes('ogg')) return 'ogg';
  return 'webm';
}

/** Read an image's intrinsic size so the bubble can reserve space before load. */
async function imageSize(file: File): Promise<{ width?: number; height?: number }> {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = url;
    });
    return { width: image.naturalWidth, height: image.naturalHeight };
  } catch {
    return {};
  } finally {
    URL.revokeObjectURL(url);
  }
}
